import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon, ShieldCheckIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@tubo/ui/components/ui/button";
import { useUiTranslation } from "@tubo/ui/i18n";
import privacyPolicyZh from "../content/privacy-policy.md?raw";
import privacyPolicyEn from "../content/privacy-policy.en.md?raw";

export const Route = createFileRoute("/privacy-policy")({
  component: PrivacyPolicyPage,
});

const policyContentZh = privacyPolicyZh
  .replace(/^# .*\n+/, "")
  .replace(/^\*\*(引言|[一二三四五六七八九十]+、.+|附则)\*\*$/gm, "## $1")
  .replace(/^\*\*(.+)\*\*$/gm, "### $1");

function PrivacyPolicyPage() {
  const { t, i18n } = useUiTranslation("auxiliaryPages");
  const isEnglish = i18n.resolvedLanguage === "en-US";
  const policyContent = isEnglish ? privacyPolicyEn.replace(/^# .*\n+/, "") : policyContentZh;
  return (
    <div className="min-h-screen bg-muted/30 text-foreground">
      <header className="border-b bg-background/95">
        <div className="mx-auto flex min-h-16 max-w-5xl items-center justify-between gap-4 px-6 sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShieldCheckIcon className="size-5" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{t("privacyPolicy.brand")}</p>
              <p className="truncate text-xs text-muted-foreground">{t("privacyPolicy.pageTitle")}</p>
            </div>
          </div>
          <Link to="/login">
            <Button variant="ghost" size="sm">
              <ArrowLeftIcon aria-hidden="true" />
              {t("privacyPolicy.backToLogin")}
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-6 py-8 sm:px-8 sm:py-12">
        <article className="rounded-xl bg-card px-6 py-8 shadow-xs ring-1 ring-foreground/10 sm:px-10 sm:py-12">
          <div className="mb-10 border-b pb-8">
            <div className="mb-4 flex size-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <ShieldCheckIcon className="size-5" aria-hidden="true" />
            </div>
            <h1 className="text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
              {t("privacyPolicy.heading")}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
              {t("privacyPolicy.introduction")}
            </p>
          </div>

          <div className="max-w-3xl text-[0.9375rem] leading-7 text-foreground/90">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ children }) => (
                  <h2 className="mt-10 text-balance text-xl font-semibold tracking-tight first:mt-0">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="mt-7 text-base font-semibold">{children}</h3>
                ),
                p: ({ children }) => <p className="mt-4 text-pretty">{children}</p>,
                ol: ({ children }) => (
                  <ol className="mt-4 list-decimal space-y-2 pl-6 marker:text-muted-foreground">
                    {children}
                  </ol>
                ),
                ul: ({ children }) => (
                  <ul className="mt-4 list-disc space-y-2 pl-6 marker:text-muted-foreground">
                    {children}
                  </ul>
                ),
                li: ({ children }) => <li className="pl-1">{children}</li>,
                strong: ({ children }) => (
                  <strong className="font-semibold text-foreground">{children}</strong>
                ),
                a: ({ children, href }) => (
                  <a
                    href={href}
                    className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {policyContent}
            </ReactMarkdown>
          </div>
        </article>
      </main>
    </div>
  );
}
